import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const BASE_CELL_SIZE = 30;

// Booth picker map used by the Returning Vendors import dialog. Renders the
// current festival's map and lets the admin toggle booths for the active
// vendor while visually flagging booths already taken by another vendor
// (either previously registered or claimed earlier in the same import
// session) and booths this vendor had last year.
const BulkImportBoothMap = ({
  open,
  onClose,
  vendor,
  booths = [],
  claimedByOthers = new Map(),
  selectedBoothIds = new Set(),
  priorCellNames = [],
  maxBooths = 3,
  onToggleBooth,
}) => {
  const priorCellNameSet = useMemo(
    () => new Set((priorCellNames || []).filter(Boolean).map(String)),
    [priorCellNames]
  );

  const { width, height, grid } = useMemo(() => {
    const w = booths.reduce((max, c) => Math.max(max, c.x_coordinate || 0), 0);
    const h = booths.reduce((max, c) => Math.max(max, c.y_coordinate || 0), 0);
    const byCoord = new Map();
    booths.forEach((c) => {
      if (!c.x_coordinate || !c.y_coordinate) return;
      byCoord.set(`${c.x_coordinate}:${c.y_coordinate}`, c);
    });
    const rows = Array.from({ length: h }, (_, y) =>
      Array.from({ length: w }, (_, x) => byCoord.get(`${x + 1}:${y + 1}`) || null)
    );
    return { width: w, height: h, grid: rows };
  }, [booths]);

  const selectedCount = selectedBoothIds.size;
  const atLimit = selectedCount >= maxBooths;

  // Resolve the visual treatment + click behavior for a single cell.
  const getCellInfo = (cell) => {
    if (!cell) {
      return { color: '#fff', clickable: false, label: '' };
    }
    if (cell.cell_type !== 'booth') {
      const palette = {
        walkway: '#e0e0e0',
        food: '#ffd180',
        empty: '#fff',
      };
      return {
        color: palette[cell.cell_type] || '#eeeeee',
        clickable: false,
        label: cell.cell_name || cell.cell_type,
      };
    }
    const isSelected = selectedBoothIds.has(cell.id);
    const claimedByOther = claimedByOthers.get(cell.id) || null;
    const isPrior = cell.cell_name && priorCellNameSet.has(String(cell.cell_name));
    const isUnavailable =
      cell.booth_status === 'unavailable' || (cell.is_registered && !isSelected);

    if (isSelected) {
      return {
        color: '#2e7d32',
        border: isPrior ? '2px solid #ffd54f' : '2px solid #1b5e20',
        clickable: true,
        label: cell.cell_name,
        tooltip: isPrior
          ? `Selected (same booth as last year)`
          : 'Selected for this vendor',
      };
    }
    if (claimedByOther) {
      return {
        color: '#ef9a9a',
        clickable: false,
        label: cell.cell_name,
        tooltip: `Claimed in this import by ${claimedByOther}`,
      };
    }
    if (isUnavailable) {
      return {
        color: cell.is_registered ? '#9e9e9e' : '#bdbdbd',
        clickable: false,
        label: cell.cell_name,
        tooltip: cell.is_registered
          ? `Already registered${cell.vendor_name ? ` to ${cell.vendor_name}` : ''}`
          : 'Unavailable',
      };
    }
    return {
      color: isPrior ? '#ffd54f' : '#1976d2',
      border: isPrior ? '2px solid #f57f17' : 'none',
      textColor: isPrior ? '#3e2723' : '#fff',
      clickable: !atLimit,
      label: cell.cell_name,
      tooltip: isPrior
        ? `Last year's booth for this vendor${atLimit ? ' (at max booths)' : ''}`
        : atLimit
          ? `At max ${maxBooths} booths — remove one first`
          : 'Click to assign',
    };
  };

  const handleCellClick = (cell) => {
    if (!cell || cell.cell_type !== 'booth') return;
    const isSelected = selectedBoothIds.has(cell.id);
    if (!isSelected) {
      if (atLimit) return;
      if (claimedByOthers.has(cell.id)) return;
      if (cell.booth_status === 'unavailable') return;
      if (cell.is_registered) return;
    }
    if (onToggleBooth) onToggleBooth(cell, !isSelected);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          Assign booths {vendor?.business_name ? `for ${vendor.business_name}` : ''}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.25 }}
        >
          {selectedCount} of {maxBooths} booths selected
          {priorCellNames?.length
            ? ` • Last year: ${priorCellNames.join(', ')}`
            : ''}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          <Chip
            size="small"
            sx={{ bgcolor: '#1976d2', color: '#fff' }}
            label="Available"
          />
          <Chip
            size="small"
            sx={{ bgcolor: '#ffd54f', color: '#3e2723', border: '2px solid #f57f17' }}
            label="Last year's booth"
          />
          <Chip
            size="small"
            sx={{ bgcolor: '#2e7d32', color: '#fff' }}
            label="Selected for this vendor"
          />
          <Chip
            size="small"
            sx={{ bgcolor: '#ef9a9a', color: '#3e2723' }}
            label="Claimed in this import"
          />
          <Chip
            size="small"
            sx={{ bgcolor: '#9e9e9e', color: '#fff' }}
            label="Registered / unavailable"
          />
        </Stack>

        {width === 0 || height === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No map data for the current festival.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              background: '#f5f6fa',
              p: 2,
              overflow: 'auto',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${width}, ${BASE_CELL_SIZE}px)`,
                gridTemplateRows: `repeat(${height}, ${BASE_CELL_SIZE}px)`,
                gap: '1px',
                background: '#888',
                mx: 'auto',
                width: 'fit-content',
                userSelect: 'none',
              }}
            >
              {grid.flat().map((cell, i) => {
                const info = getCellInfo(cell);
                const content = (
                  <Box
                    sx={{
                      width: BASE_CELL_SIZE,
                      height: BASE_CELL_SIZE,
                      background: info.color,
                      border: info.border || 'none',
                      boxSizing: 'border-box',
                      cursor: info.clickable ? 'pointer' : 'default',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: info.clickable
                          ? '0 0 0 2px #1976d2'
                          : 'none',
                        zIndex: 1,
                      },
                    }}
                    onClick={() => handleCellClick(cell)}
                  >
                    {cell?.cell_name && cell?.cell_type === 'booth' && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '12px',
                          lineHeight: 1,
                          color: info.textColor || '#fff',
                          textAlign: 'center',
                          width: '100%',
                          pointerEvents: 'none',
                        }}
                      >
                        {cell.cell_name}
                      </Typography>
                    )}
                  </Box>
                );
                return info.tooltip ? (
                  <Tooltip key={i} title={info.tooltip} arrow>
                    {content}
                  </Tooltip>
                ) : (
                  <React.Fragment key={i}>{content}</React.Fragment>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportBoothMap;
